import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaSpecialtyCreate,
  schemaSpecialtyEdit,
} from '../schemas/yup.specialty.schema';
import { ISpecialty } from '../../../shared/interfaces';
import { patchSpecialty, postSpecialty } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface ISpecialtyProps {
  open: boolean;
  close: () => void;
  specialty: ISpecialty | null;
  setSpecialties: React.Dispatch<React.SetStateAction<ISpecialty[]>>;
}

const SpecialtyCreateEdit: React.FC<ISpecialtyProps> = ({
  open,
  close,
  specialty,
  setSpecialties,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaSpecialtyCreate
      .validate({
        description,
      })
      .then(async (validated) => {
        await postSpecialty(validated)
          .then((data) => {
            setSpecialties((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('specialtiesModal.toast.successTitle'),
              description: t('specialtiesModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('specialtiesModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('specialtiesModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('specialtiesModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, t, addToast, close, setSpecialties]);

  const handleEdit = useCallback(async () => {
    schemaSpecialtyEdit
      .validate({
        description,
        status,
      })
      .then(async (validated) => {
        await patchSpecialty(specialty?.id, validated)
          .then((data) => {
            setSpecialties((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('specialtiesModal.toast.infoTitle'),
              description: t('specialtiesModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('specialtiesModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('specialtiesModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('specialtiesModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, t, addToast, close, setSpecialties, specialty]);

  useEffect(() => {
    if (specialty) {
      setDescription(specialty.description);
      setStatus(!!specialty.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        specialty ? (
          <>
            <EditOutlined /> {t('specialtiesModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('specialtiesModal.titleCreate')}
          </>
        )
      }
      onOk={specialty ? handleEdit : handleCreate}
    >
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item label={t('specialtiesModal.fields.description')}>
          <AInput
            placeholder={t('specialtiesModal.fields.descriptionPlaceholder')}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        {specialty && (
          <Form.Item label={t('specialtiesModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default SpecialtyCreateEdit;
